import React from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/lib/Async";
import { NotificationManager } from "react-notifications";
import { NumberField, TextField } from "Utils/renderField/fieldsHookForms";
import {
    RenderKilogramo,
    RenderNumber,
} from "Utils/renderField/renderReadField";
import { useDebouncedAsyncSelect } from "Utils/Hooks/useDebounce";
import { TIPO_PACAS } from "../hooks/useInventarioInicialForm";
import NullableValue from "../../../../../../../../Utils/renderField/nullableValue";
const eliminarIcon = require("assets/img/icons/borrar.png");

const OPCIONES_CONFORMIDAD = [
    { value: true, label: "Conforme" },
    { value: false, label: "No conforme" },
];

const CAMPOS_FILA = ["producto", "conformidad", "cantidad", "identificador"];

// el pallet contado se registra con el número que ya tiene físicamente, con el
// mismo relleno de ceros que usa el sistema: 45-2026 queda como 045-2026
const FORMATO_NUMERO_PALLET = /^\d{1,8}-\d{4}$/;

const formatearNumeroPallet = (numero) => {
    const partes = numero.split("-");

    let correlativo = partes[0];

    while (correlativo.length < 3) {
        correlativo = `0${correlativo}`;
    }

    return `${correlativo}-${partes[1]}`;
};

// Captura de una de las dos tablas del inventario inicial. Las pacas se
// registran por pallet físico, así que un mismo producto puede repetirse; el
// sobrante es un solo saldo por producto y conformidad.
export const DetalleList = ({ hook, tipo }) => {
    const esPacas = tipo === TIPO_PACAS;

    const detalles = esPacas ? hook.detallesPacas : hook.detallesSobrante;

    const {
        control,
        setValue,
        getValues,
        trigger: validarFila,
        formState: { errors },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            producto: null,
            conformidad: OPCIONES_CONFORMIDAD[0],
            cantidad: null,
            identificador: "",
        },
    });

    const loadProductos = useDebouncedAsyncSelect(function (search) {
        return hook.getProductos({ search: search });
    });

    const LimpiarFila = () => {
        CAMPOS_FILA.forEach((campo) => {
            if (campo === "conformidad") {
                setValue(campo, OPCIONES_CONFORMIDAD[0]);
                return;
            }

            setValue(campo, campo === "identificador" ? "" : null);
        });
    };

    // el pallet ya existía antes de usar el sistema, así que su número tiene
    // que ser anterior al año y al correlativo desde los que numera el sistema
    const NumeroPalletValido = (numero) => {
        const partes = numero.split("-");
        const correlativo = Number(partes[0]);
        const anio = Number(partes[1]);

        const anioInicial = hook.correlativo.anio;
        const correlativoInicial = hook.correlativo.correlativoInicial;

        if (anio > anioInicial) {
            NotificationManager.warning(
                `La numeración del sistema inicia en ${anioInicial}, por lo que los pallets de ${anio} los numera el sistema`,
                "Advertencia",
                3000,
            );
            return false;
        }

        if (
            anio === anioInicial &&
            correlativoInicial &&
            correlativo >= correlativoInicial
        ) {
            NotificationManager.warning(
                `Los pallets de ${anioInicial} desde el ${correlativoInicial} en adelante los numera el sistema`,
                "Advertencia",
                3000,
            );
            return false;
        }

        return true;
    };

    const AgregarFila = async () => {
        const valido = await validarFila();
        if (!valido) return;

        const { producto, conformidad, cantidad, identificador } = getValues();

        const cantidadNumero = Number(cantidad);

        if (!cantidadNumero || cantidadNumero <= 0) {
            NotificationManager.warning(
                esPacas
                    ? "Las pacas deben ser mayores a cero"
                    : "Los kg secos deben ser mayores a cero",
                "Advertencia",
                3000,
            );
            return;
        }

        const numeroPallet = esPacas
            ? formatearNumeroPallet((identificador || "").trim())
            : null;

        if (esPacas) {
            if (!NumeroPalletValido(numeroPallet)) return;

            const repetido = detalles.some(
                (detalle) => detalle.identificador_fisico === numeroPallet,
            );

            if (repetido) {
                NotificationManager.warning(
                    `El pallet ${numeroPallet} ya fue agregado`,
                    "Advertencia",
                    3000,
                );
                return;
            }
        }

        // el sobrante es un saldo por producto y conformidad, no se captura
        // por bulto como los pallets
        if (!esPacas) {
            const repetido = detalles.some(
                (detalle) =>
                    detalle.producto_id === producto.id &&
                    detalle.es_conforme_bodega === conformidad.value,
            );

            if (repetido) {
                NotificationManager.warning(
                    `El sobrante ${conformidad.label.toLowerCase()} de ${producto.nombre} ya fue agregado`,
                    "Advertencia",
                    3000,
                );
                return;
            }
        }

        hook.agregarDetalle(tipo, {
            producto_id: producto.id,
            producto_nombre: producto.nombre,
            es_conforme_bodega: conformidad.value,
            pacas: esPacas ? cantidadNumero : 0,
            kg_secos: esPacas ? 0 : cantidadNumero,
            identificador_fisico: numeroPallet,
        });

        LimpiarFila();
    };

    const total = detalles.reduce(
        (acumulado, detalle) =>
            acumulado + Number(esPacas ? detalle.pacas : detalle.kg_secos),
        0,
    );

    return (
        <div className="inventario-inicial__tabla">
            <h4 className="mb-0">
                {esPacas ? "Pacas de pallets" : "Sobrante"}
            </h4>

            {hook.editable && (
                <div className="formulario-fields-row w-100 gap-1rem flex-wrap align-items-start">
                    <div
                        className="formulario-field"
                        style={{ minWidth: "180px", flex: 2 }}
                    >
                        <label className="text-nowrap">Producto</label>
                        <Controller
                            name="producto"
                            control={control}
                            rules={{ required: "Debe seleccionar un producto" }}
                            render={({ field }) => (
                                <AsyncSelect
                                    {...field}
                                    className="react-select-container"
                                    isSearchable
                                    isClearable
                                    defaultOptions
                                    loadOptions={loadProductos}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nombre}
                                    placeholder="Seleccionar"
                                />
                            )}
                        />
                        {errors.producto && (
                            <small className="text-danger">
                                {errors.producto.message}
                            </small>
                        )}
                    </div>

                    <div
                        className="formulario-field"
                        style={{ minWidth: "150px" }}
                    >
                        <label className="text-nowrap">Condición</label>
                        <Controller
                            name="conformidad"
                            control={control}
                            rules={{ required: "Debe indicar la condición" }}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    className="react-select-container"
                                    options={OPCIONES_CONFORMIDAD}
                                    placeholder="Seleccionar"
                                />
                            )}
                        />
                    </div>

                    <div
                        className="formulario-field"
                        style={{ minWidth: "120px" }}
                    >
                        <label className="text-nowrap">
                            {esPacas ? "Pacas" : "Kg secos"}
                        </label>
                        <NumberField
                            control={control}
                            errors={errors}
                            name="cantidad"
                            decimalScale={esPacas ? 0 : 2}
                            allowNegative={false}
                            rules={{
                                required: esPacas
                                    ? "Debe indicar las pacas"
                                    : "Debe indicar los kg secos",
                            }}
                        />
                    </div>

                    {esPacas && (
                        <div
                            className="formulario-field"
                            style={{ minWidth: "150px" }}
                        >
                            <label className="text-nowrap">
                                Número de pallet
                            </label>
                            <TextField
                                control={control}
                                errors={errors}
                                name="identificador"
                                placeholder="Ej. 425-2026"
                                rules={{
                                    required: "Debe indicar el número",
                                    pattern: {
                                        value: FORMATO_NUMERO_PALLET,
                                        message:
                                            "El formato debe ser correlativo-año, por ejemplo 425-2026",
                                    },
                                }}
                            />
                        </div>
                    )}

                    <div
                        className="formulario-field mb-0 ml-auto no-flex"
                        style={{
                            alignSelf: "stretch",
                            maxHeight: 57,
                            display: "flex",
                        }}
                    >
                        <button
                            className="btn btn-primary text-nowrap mt-auto"
                            onClick={AgregarFila}
                        >
                            Agregar
                        </button>
                    </div>
                </div>
            )}

            <div className="new-table-wrapper mt-2">
                <table className="table-striped">
                    <thead>
                        <tr>
                            <th>Producto</th>
                            <th>Condición</th>
                            {esPacas && <th>Número de pallet</th>}
                            {esPacas && <th>Pallet generado</th>}
                            <th>{esPacas ? "Pacas" : "Kg secos"}</th>
                            {hook.editable && <th>Acciones</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {detalles.length === 0 && (
                            <tr>
                                <td
                                    colSpan={esPacas ? 6 : 4}
                                    className="text-center"
                                >
                                    Sin existencia registrada
                                </td>
                            </tr>
                        )}
                        {detalles.map((detalle, index) => (
                            <tr key={`${tipo}-${index}`}>
                                <td className="text-nowrap text-center">
                                    {detalle.producto_nombre}
                                </td>
                                <td className="text-nowrap text-center">
                                    {detalle.es_conforme_bodega
                                        ? "Conforme"
                                        : "No conforme"}
                                </td>
                                {esPacas && (
                                    <td className="text-nowrap text-center">
                                        <NullableValue
                                            value={detalle.identificador_fisico}
                                        >
                                            {detalle.identificador_fisico || ""}
                                        </NullableValue>
                                    </td>
                                )}
                                {esPacas && (
                                    <td className="text-nowrap text-center">
                                        {detalle.numero_pallet || (
                                            <span className="text-muted">
                                                Se genera al guardar
                                            </span>
                                        )}
                                    </td>
                                )}
                                <td className="text-nowrap text-center">
                                    {esPacas ? (
                                        <RenderNumber
                                            value={detalle.pacas}
                                            decimalScale={0}
                                        />
                                    ) : (
                                        <RenderKilogramo
                                            value={detalle.kg_secos}
                                            decimalScale={2}
                                        />
                                    )}
                                </td>
                                {hook.editable && (
                                    <td className="text-nowrap text-center">
                                        <img
                                            src={eliminarIcon}
                                            alt="Eliminar"
                                            title="Eliminar"
                                            className="action_img"
                                            onClick={() =>
                                                hook.EliminarDetalle(
                                                    tipo,
                                                    index,
                                                )
                                            }
                                        />
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                    {detalles.length > 0 && (
                        <tfoot>
                            <tr className="totales-row">
                                <td
                                    className="text-right"
                                    colSpan={esPacas ? 4 : 2}
                                >
                                    Total
                                </td>
                                <td className="text-nowrap text-center">
                                    {esPacas ? (
                                        <RenderNumber
                                            value={total}
                                            decimalScale={0}
                                        />
                                    ) : (
                                        <RenderKilogramo
                                            value={total}
                                            decimalScale={2}
                                        />
                                    )}
                                </td>
                                {hook.editable && <td />}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
};
